import { useCallback, useContext, useEffect } from "react";
import { useRouter } from "next/router";
import ApplicationContext, { AnalyticsState } from "@/contexts/ApplicationContext";

const consentTtl = 1000 * 60 * 60 * 24 * 30; // 30 days

export const useConsent = (): {
  consentWasGiven: boolean | undefined;
  clearConsent: () => void;
  giveConsent: () => void;
  denyConsent: () => void;
  setAnalyticsState: (newState: AnalyticsState) => void;
} => {
  const router = useRouter();

  const {
    analytics: { consent, consentDecision },
    setAnalyticsState,
  } = useContext(ApplicationContext);

  const giveConsent = useCallback(
    () => setAnalyticsState({ consent: true, consentDecision: Date.now() }),
    [setAnalyticsState],
  );

  const clearConsent = useCallback(() => {
    setAnalyticsState({ consent: undefined, consentDecision: undefined });

    // reload to remove tracking scripts
    router.reload();
  }, [setAnalyticsState, router]);

  const denyConsent = useCallback(
    () => setAnalyticsState({ consent: false, consentDecision: Date.now() }),
    [setAnalyticsState],
  );

  const consentDecisionWasLongAgo =
    consent === false && consentDecision !== undefined && Date.now() - consentDecision > consentTtl;

  useEffect(() => {
    if (consentDecisionWasLongAgo) {
      // Clear the consent state after some time and ask for consent again
      clearConsent();
    }
  }, [clearConsent, consentDecisionWasLongAgo]);

  return {
    consentWasGiven: consent,
    giveConsent,
    denyConsent,
    clearConsent,
    setAnalyticsState,
  };
};
